import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { Formula, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.coste-teorico-vs-coste-real-cocina.${field}`

const SOURCES: { labelKey: string; fallback: string }[] = [
    {
        labelKey: 'sources.1',
        fallback:
            'Fórmula de coste real de producto (inventario inicial + compras − inventario final): criterio operativo habitual de control de costes en restauración. Ejemplo numérico: elaboración propia de Tipi a partir del escandallo de hamburguesa de la serie (3,95 € × 500 raciones). No es el dato de un local concreto.',
    },
    {
        labelKey: 'sources.2',
        fallback:
            'Cifras de coste a ojo frente a coste real (3 € vs 3,95 €) y volumen de 500 unidades al mes: reutilizadas del artículo «Cómo calcular cuánto cuesta cada plato» de esta misma serie Tipi.',
    },
]

export function CosteTeoricoRealArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'creencia', label: t(k('toc.1'), '1. La creencia del escandallo perfecto') },
        { id: 'pega', label: t(k('toc.2'), '2. Por qué el papel no controla la cocina') },
        { id: 'cuenta', label: t(k('toc.3'), '3. La desviación con números') },
        { id: 'letra', label: t(k('toc.4'), '4. Qué mirar cuando teórico y real no cuadran') },
        { id: 'semana', label: t(k('toc.5'), '5. Tres comprobaciones esta semana') },
        { id: 'matices', label: t(k('toc.6'), '6. Cuándo el escandallo sí basta (y cuándo no)') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Puedes tener unos escandallos impecables y seguir perdiendo dinero si nadie respeta lo que aparece en ellos. El documento no controla el negocio por sí solo.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Cocinero contrastando una receta en papel con lo que hay realmente en la sartén',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                {t(k('updated'), 'Actualizado el 13 de septiembre de 2026.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.belief'),
                                    'Toda la vida igual: si el escandallo está bien hecho, el negocio está controlado. Parece de sentido común. Has pesado, has apuntado, has actualizado precios. El Excel cuadra. ¿Qué más hace falta?',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                <span className={articleUi.em}>
                                    {t(
                                        k('intro.em'),
                                        'El escandallo perfecto describe lo que debería pasar. El coste real describe lo que pasó. Mientras no compares los dos, el papel te está mintiendo con educación.',
                                    )}
                                </span>
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='creencia' className={articleUi.h2}>
                        {t(k('h2.creencia'), 'Por qué parece verdad (y qué parte lo es)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.creencia.1Before'),
                                'La creencia se sostiene porque el escandallo sí es el punto de partida. Sin ',
                            )}
                            <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                {t(k('linkEscandallo'), 'escandallo paso a paso')}
                            </LearnArticleLink>
                            {t(k('p.creencia.1Mid'), ' no tienes coste por ración. Sin ')}
                            <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                {t(k('linkFoodCost'), 'food cost y margen')}
                            </LearnArticleLink>
                            {t(
                                k('p.creencia.1After'),
                                ' no sabes qué deja cada venta. Eso es cierto. El salto falso es creer que el documento ya cierra el círculo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.creencia.2'),
                                'El teórico asume gramajes respetados, precios al día, merma contada y cero invitaciones sueltas. En cocina real hay turnos, prisas y un «échale un poco más» que el Excel no ve.',
                            )}
                        </p>
                    </div>

                    <h2 id='pega' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.pega'), 'Por qué el papel no controla la cocina')}
                    </h2>
                    <div className={articleUi.compare}>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>
                                {t(k('compare.theoretical.label'), 'Coste teórico')}
                            </p>
                            <p className={articleUi.compareTitle}>
                                {t(k('compare.theoretical.title'), 'Lo que debería ocurrir.')}
                            </p>
                            <p className={articleUi.compareBody}>
                                {t(
                                    k('compare.theoretical.body'),
                                    'Si se respetan los gramajes, no existe desperdicio innecesario, no se tira producto, no hay errores, no existen caducidades y los precios están actualizados.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabelAlert}>{t(k('compare.actual.label'), 'Coste real')}</p>
                            <p className={articleUi.compareTitle}>
                                {t(k('compare.actual.title'), 'Lo que ocurre realmente.')}
                            </p>
                            <p className={articleUi.compareBody}>
                                {t(
                                    k('compare.actual.body'),
                                    'El cocinero que pone un poco más. La caja que llega con menos unidades. La salsa que se prepara de más. El producto que caduca. La merma que nadie apunta.',
                                )}
                            </p>
                        </div>
                    </div>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.pega.1'),
                                'La pega va primero: un escandallo sin contraste de inventario te da tranquilidad falsa. Crees que el food cost de carta es el del mes. A veces lo es. A menudo no.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.formulaIntro'),
                                'La fórmula para calcular el coste real de producto durante un periodo es:',
                            )}
                        </p>
                        <Formula>
                            {t(
                                k('formula.actualProduct'),
                                'Coste real de producto = Inventario inicial + Compras – Inventario final',
                            )}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.pega.2'),
                                'Ese número, el consumo real, es el que pagaste. El teórico es la suma de escandallos × unidades vendidas. La diferencia entre ambos es la desviación: porciones grandes, sobreproducción, caducidades, invitaciones, errores, robos o precios sin actualizar.',
                            )}
                        </p>
                    </div>

                    <h2 id='cuenta' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.cuenta'), 'La desviación con números (el mes de la hamburguesa)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cuenta.1'),
                                'Retomemos la hamburguesa de la serie: 3,95 € de coste por ración en escandallo. Carlos vende 500 al mes. En papel, el teórico de ese plato son 1.975 €.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cuenta.2'),
                                'Al cerrar el mes hace inventario y aplica la fórmula. Inventario inicial de esa línea + compras − inventario final = 2.450 € consumidos. La diferencia es 475 €.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.cuenta.em'),
                                    '475 € al mes de desviación en un solo plato. En doce meses, 5.700 €. Misma magnitud que cuando calculó mal el coste a ojo: ahora el escandallo está bien y aun así se le escapa el margen.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.pItalic, articleUi.pGap2xl)}>
                            {t(
                                k('p.cuenta.note'),
                                'Estimación ilustrativa a partir del ejemplo de hamburguesa de la serie (3,95 € × 500 raciones). No es el dato de un local concreto.',
                            )}
                        </p>
                    </div>

                    <h2 id='letra' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.letra'), 'Qué mirar cuando teórico y real no cuadran')}
                    </h2>
                    <div className={articleUi.errorGrid}>
                        <div className={articleUi.errorCard}>
                            <h3>{t(k('error.1.title'), 'Calcular «más o menos»')}</h3>
                            <p>
                                {t(
                                    k('error.1.body'),
                                    'Si el gramaje es aproximado, el teórico ya nace torcido. Báscula y ficha clara antes de culpar al inventario.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.errorCard}>
                            <h3>{t(k('error.2.title'), 'No actualizar los precios')}</h3>
                            <p>
                                {t(
                                    k('error.2.body'),
                                    'El teórico usa el precio viejo; las compras del mes van al real. La desviación parece «merma» y es inflación del proveedor.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.errorCard}>
                            <h3>{t(k('error.3.title'), 'Ignorar la merma')}</h3>
                            <p>
                                {t(
                                    k('error.3.body'),
                                    'Un kilo comprado no es un kilo útil. Si el escandallo no lleva rendimiento, el teórico queda corto siempre.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.errorCard}>
                            <h3>{t(k('error.4.title'), 'Olvidar guarniciones y pequeños consumos')}</h3>
                            <p>
                                {t(
                                    k('error.4.body'),
                                    'Pan, salsas, aceite, decoración y envases salen del almacén y no del papel. El real los cuenta; el teórico, no.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.errorCard}>
                            <h3>{t(k('error.5.title'), 'Utilizar el precio con IVA')}</h3>
                            <p>
                                {t(
                                    k('error.5.body'),
                                    'Mezclar venta con IVA y coste sin IVA infla el margen en el papel y no explica la desviación del almacén.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.errorCard}>
                            <h3>{t(k('error.6.title'), 'Pensar que un buen food cost garantiza rentabilidad')}</h3>
                            <p>
                                {t(
                                    k('error.6.body'),
                                    'Un % bonito en carta puede dejar pocos euros o mucho personal. El teórico vs real solo habla de producto.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.errorCard}>
                            <h3>{t(k('error.7.title'), 'Hacer el escandallo y guardarlo en un cajón')}</h3>
                            <p>
                                {t(
                                    k('error.7.body'),
                                    'Sin contraste periódico, el documento es decoración. La herramienta viva es la comparación mes a mes.',
                                )}
                            </p>
                        </div>
                    </div>

                    <h2 id='semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.semana'), 'Tres comprobaciones esta semana')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.semana.1'),
                                '1. Elige tu plato estrella con escandallo al día. Multiplica coste por ración × unidades vendidas del último mes cerrado. Ese es tu teórico de ese plato.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.semana.2'),
                                '2. Si puedes, valora inventario inicial y final de los ingredientes clave de ese plato (o del conjunto de cocina si aún no tienes trazabilidad fina). Aplica la fórmula. Anota la diferencia en euros, no solo en %.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.semana.2LinkBefore'),
                                'Si aún no cierras ese conteo, empieza por ',
                            )}
                            <LearnArticleLink slug='como-hacer-inventario-hosteleria'>
                                {t(k('linkInventario'), 'cómo hacer un inventario en hostelería')}
                            </LearnArticleLink>
                            {t(
                                k('p.semana.2LinkAfter'),
                                ': 20 o 30 referencias por euros, el último día del mes.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.semana.3'),
                                '3. Si la desviación supera lo que estás dispuesto a «asumir a ojo», no abras otro Excel de recetas: primero pesa en el pase, corta la sobreproducción y actualiza el albarán que cambió de precio. El papel se arregla después del hábito.',
                            )}
                        </p>
                    </div>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'Cuándo el escandallo sí basta (y cuándo no)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'Si cocinas tú casi todos los servicios, la carta es corta y el inventario es pequeño, el teórico y el real suelen acercarse. Ahí el escandallo bien hecho sí te da control casi completo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.2'),
                                'Si hay turnos, personal nuevo, delivery, invitaciones o producto de merma alta, el hueco crece. No significa que el escandallo sea inútil: significa que sin contraste de almacén estás midiendo la intención, no el resultado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.3'),
                                'Tampoco confundas desviación con robo. La mayoría de las veces es porción, caducidad o precio. Empieza por lo aburrido antes de sospechar de nadie.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.matices.4Before'), 'Desde ')}
                            <TipiLink />
                            {t(
                                k('p.matices.4After'),
                                ' te ayudamos a ver dónde se abre el hueco entre lo que debería costar y lo que consumiste. No inventamos el inventario por ti: ponemos números a la desviación.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.5'),
                                'Si el papel te dice una cosa y el almacén otra, cuéntanoslo y le ponemos números.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('closing.signoff'), 'El papel no cocina. El que cocina, sí.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.outroBefore'),
                                'Cuando teórico y real empiezan a hablarse, el siguiente paso suele ser ',
                            )}
                            <LearnArticleLink slug='disenar-carta-rentable-restaurante'>
                                {t(k('linkCarta'), 'diseñar una carta más rentable')}
                            </LearnArticleLink>
                            {t(k('p.outroAfter'), '.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿El escandallo cuadra y el almacén no?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi ves el coste teórico del plato y cuándo las compras se comen ese número. Cuéntanoslo y le ponemos números.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>{t(k(source.labelKey), source.fallback)}</li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
